/**
 * VE-29142 — hold the TDO lock on documents the reviewer has unsaved work in,
 * not just the one on screen.
 *
 * THE RULE, deliberately narrow: a lock is held for the document being edited
 * (already handled — see the heartbeat in `pages/DocumentDetails`) plus any
 * document visited in this session that still has unsaved changes. Nothing
 * else. A file merely looked at and left clean holds nothing, because taking a
 * lock for it would block colleagues from work the reviewer is not doing.
 *
 * WHY IT IS NEEDED. `suspendedSessions` holds exactly the dirty-and-switched-
 * away-from documents, and `DocumentDetails` releases its lock on unmount —
 * which a switch triggers. So before this, a reviewer could leave unsaved
 * redactions in a file and a colleague could open and edit it freely, with the
 * reviewer's Save All later writing straight over their work.
 *
 * WHY A SAGA. The component can only know about the document it is rendering.
 * The lock assessment already names this as the right shape —
 * `plans/document-editor-lock-assessment.md` §2 weakness 3 ("lifecycle in the
 * component … belongs in a saga") and §4 ("saga-owned lifecycle").
 *
 * WHAT THIS DOES NOT DO. It does not touch the active document's lock, which
 * keeps its existing component-driven heartbeat for parity with the video
 * editor. Two lifecycles is not the end state — §4 wants one — but collapsing
 * them means rewriting the video path too, and that is not this ticket.
 *
 * HOW IT TAKES THE LOCK, and why not the way everything else does. Background
 * documents go through `serviceRefreshBackgroundTdoLock`, NOT `checkTdoLock`.
 * The latter's handlers write the single global `tdoLockInfo` without reading
 * the tdoId off the response, which is safe only while the caller is the
 * document on screen — fired for a parked one it overwrites the on-screen
 * document's verdict, and can clear a genuine lock on the file being edited.
 * The service writes our own record and asks nothing about ownership. See its
 * comment in `../services`.
 *
 * The lock remains ADVISORY and race-prone on acquire (§2 weaknesses 1 and 5);
 * this widens WHICH documents are covered, and changes nothing about how good
 * the covering is.
 */
import { delay, put, race, select, take } from 'typed-redux-saga/macro';

import { LOCK_REFRESH } from '@helpers/constants';
// The service thunk, not the REMOVE_TDO_LOCK action: that action is consumed
// by `watchRemoveTdoLock`, which is `takeLatest`. Fanning N releases through a
// single-flight watcher works today only because its body is one
// non-suspending `put` — an `await` added there would cancel every release but
// the last, and leak locks silently.
import { removeTdoLock as removeTdoLockService } from '@common-modules/mediaDetails/services';
import { selectUser } from '@helpers/tdoHelper';
import { selectFeatureFlags } from '@common/user-permissions';
import { modules } from '@veritone/glc-redux';
import { CLOSE_DOCUMENT, OPEN_DOCUMENT_SUCCESS } from '../actions';
import { isSessionWorthKeeping } from '../models';
import { selectSession, selectSuspendedSessions } from '../selectors';
import { serviceRefreshBackgroundTdoLock } from '../services';

const {
  user: { LOGOUT },
} = modules;

export function* suspendedLocksSaga() {
  // Ids this saga believes it holds, so it can tell an acquire from a refresh
  // and release exactly what it took. Local rather than in the store: it is
  // this loop's bookkeeping, and nothing else should act on it.
  let held: string[] = [];

  /**
   * Release several locks.
   *
   * One `put` per id would go through `watchRemoveTdoLock`, which is
   * `takeLatest` — single-flight by construction. It happens to work today
   * only because that watcher's body is a single non-suspending `put`; the day
   * someone adds an `await` to it, every release but the last is cancelled and
   * locks leak with nothing to show for it. Depending on an unstated
   * scheduling property of a watcher this module does not own is not worth the
   * one-line saving, so this drives the thunk itself.
   */
  const release = function* (ids: string[], userId: string) {
    for (const tdoId of ids) {
      yield* put(removeTdoLockService({ tdoId, userId }));
    }
  };

  while (true) {
    const { flags, user } = {
      flags: yield* select(selectFeatureFlags),
      user: yield* select(selectUser),
    };
    const userId = user?.userId;

    if (flags?.tdoLock && userId) {
      // Filtered, not assumed. The reducer only PARKS a dirty document, but
      // Save All persists suspended documents in place, so a parked session
      // can go clean while it is off screen. Holding its lock after that keeps
      // colleagues out of a file nobody is editing, for the rest of the
      // editor session — the release below then picks it up on the same tick.
      const suspended = yield* select(selectSuspendedSessions);
      // `isSessionWorthKeeping`, not `hasUnsavedWork`: the latter excludes
      // `saving`, so a heartbeat landing mid-Save-All released the lock on the
      // document being written. If that save then failed, nothing re-acquired
      // it until the next tick — up to a full LOCK_REFRESH unlocked with real
      // unsaved work in it.
      const wanted = Object.values(suspended)
        .filter((session) => isSessionWorthKeeping(session.save))
        .map((session) => String(session.tdoId));

      // Anything we hold that is no longer wanted has been saved, restored as
      // the active document, or discarded.
      //
      // The ACTIVE document is excluded explicitly, and that exclusion is
      // load-bearing rather than tidy: a restored document leaves `wanted` the
      // instant OPEN_DOCUMENT_SUCCESS empties its suspended entry, so without
      // this it is "stale" on the very next tick — and `removeTdoLock` deletes
      // every one of this user's records for the TDO, including the one
      // `DocumentDetails` took milliseconds earlier. That unlocked the file
      // under active editing for up to a further heartbeat, which is precisely
      // the window a colleague needs to take it.
      const active = yield* select(selectSession);
      const stale = held.filter(
        (id) => !wanted.includes(id) && id !== active?.tdoId
      );
      if (stale.length) {
        yield* release(stale, userId);
      }

      // Refresh every wanted id, acquiring the new ones as a side effect —
      // the service is the same call for both, which is what keeps this loop
      // free of acquire/refresh branching.
      //
      // NOT `checkTdoLock`: that action's handlers write the single global
      // `tdoLockInfo` without reading the tdoId off the response, so firing it
      // for a background document overwrites the ON-SCREEN document's lock
      // verdict — in the worst direction, clearing a genuine lock and handing
      // two reviewers the same file. `serviceRefreshBackgroundTdoLock` writes
      // only our own record and asks nothing about ownership; assessing that
      // is the on-screen document's job, and `DocumentDetails` does it on the
      // way in, so a document switched back to is still judged correctly.
      const name = `${user?.kvp?.firstName ?? ''} ${
        user?.kvp?.lastName ?? ''
      }`.trim();
      for (const tdoId of wanted) {
        yield* put(serviceRefreshBackgroundTdoLock({ tdoId, userId, name }));
      }
      held = wanted;
    }

    // Wake on the heartbeat, on the editor closing, or on the set of open
    // documents changing. CLOSE_DOCUMENT clears `suspendedSessions`, so
    // waiting a full minute to notice would leave a minute of locks on
    // documents nobody has open. OPEN_DOCUMENT_SUCCESS matters for the
    // opposite reason: it is the action that parks the outgoing document, and
    // `DocumentDetails` has already released that document's lock on the route
    // change — so a just-suspended dirty document sits UNLOCKED until the next
    // tick, up to a full LOCK_REFRESH, which is exactly the gap this saga
    // exists to close. (SWITCH_DOCUMENT_STARTED fires before the suspend, so
    // waking on it would only cost a pass that finds nothing new.)
    const { closed } = yield* race({
      tick: delay(LOCK_REFRESH),
      // LOGOUT as well as CLOSE_DOCUMENT: on a shared workstation the next
      // user must not inherit locks this one is still holding, and logging out
      // does not route through the editor's close.
      closed: take([CLOSE_DOCUMENT, LOGOUT]),
      switched: take(OPEN_DOCUMENT_SUCCESS),
    });

    if (closed && held.length) {
      // `userId` from the top of THIS pass, not a fresh select. On LOGOUT the
      // user slice may already be cleared by the time we resume, and re-reading
      // it then skipped the release while `held = []` threw the bookkeeping
      // away anyway — so the locks survived to the server-side expiry, which is
      // exactly the shared-workstation case this branch exists for.
      if (userId) {
        yield* release(held, userId);
        held = [];
      }
    }
  }
}
